/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@store/index'
import { useAiReceptionistActorId } from '@hooks/useAiReceptionistActorId'
import moment from 'moment'
import {
  DragAndDrop,
  EventSettingsModel,
  Inject,
  PopupOpenEventArgs,
  RenderCellEventArgs,
  Resize,
  ScheduleComponent,
  TimelineMonth,
  TimelineViews,
  TimeScaleModel,
  ViewDirective,
  ViewsDirective,
  WorkWeek,
} from '@syncfusion/ej2-react-schedule'
import {
  CallConfirmation,
  DeleteConfirmationModal,
} from '@components/layout/Modals'
import {
  deleteAppointment,
  updateAppointment,
  updateAppointmentStatus,
} from '@store/actions/appointment.action'
import {
  AppointmentRequest,
  AppointmentResponse,
  AvailabilityStatus,
  IOperatory,
} from '@dataTypes/appointment.type'
import {
  getCancelScheduleText,
  majorSlotTemplate,
  handleEventRendered,
  canConfirmMoveAppointment,
  MoveAppointmentModal,
  usePaintAppointmentAiIcons,
} from '../DayCalendar/DayCalendarTemplate'
import './WeekCalendar.style.css'
import { CustomDateTemplate } from './WeekCalendarTemplate'
import CallerModal from '@components/layout/Modals/CallerModal/CallerModal.component'
import { formatDateTime } from '@utils/time.util'
import { firstAppointedProviderId } from '@utils/provider.util'
import EmailSender from '@components/layout/Modals/EmailSender/EmailSender.component'
import {
  IAppointmentDetails,
  IModalState,
} from '../DayCalendar/DayCalendar.component'
import RightClickMenu from '@components/pages/Appointments/RightClickMenu.component'
import RescheduleUnavailableModal, {
  getSlotUnavailableContent,
  noAvailableProvidersContent,
  slotUnavailableNamesFromAppointment,
} from '@components/pages/Appointments/RescheduleUnavailableModal.component'
import { availableProviders } from '@services/api/appointment.api'
import { Provider } from '@dataTypes/provider.types'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createCanceledAppointmentToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { INITIAL_SCHEDULER_SETTINGS_DATA } from '@components/pages/Settings/System/Scheduler/Scheduler.component'
import { ExtendedSchedulerSettingsType } from '@pages/Appointment/Appointment.page'

import { logger } from '@utils/safeLogger'

type WeekCalendarProps = {
  selectDate: string | Date
  appointmentList: AppointmentResponse[]
  providerList: Provider[]
  operatoryList: IOperatory[]
  isHipaa: boolean
  apptModalToggle: (isOpen: boolean, appt?: AppointmentResponse) => void
  setAppointmentDetails: React.Dispatch<
    React.SetStateAction<IAppointmentDetails>
  >
  onViewLoaded?: () => void
  heightOffset?: number
  onViewNote?: (input: {
    appointmentId: string
    patientId: string
    contextName: string
  }) => void
  schedulerSettings?: ExtendedSchedulerSettingsType
  allowDragAndDrop?: boolean
  allowResizing?: boolean
  /** AI receptionist user id. Omitted callers load it themselves. */
  aiActorId?: string | null
}

const CUSTOM_FIELDS = {
  id: 'Id',
  subject: { name: 'reason_for_visit' },
  startTime: { name: 'appointment_start_date' },
  endTime: { name: 'appointment_end_date' },
  location: { name: 'clinic_id' },
  provider_id: { name: 'provider_id' },
  operatory_id: { name: 'operatory_id' },
} as const

const VIEW_LOADED_DELAY_MS = 100

const INJECT_SERVICES = [
  WorkWeek,
  TimelineViews,
  TimelineMonth,
  Resize,
  DragAndDrop,
] as const

function cancelIfNoChanges(args: any, previousData?: any): boolean {
  const updatedData: AppointmentResponse = args.data
  const prevDate = args.data.appt_date
  const prevStartTime = args.data.appt_start
  const prevEndTime = args.data.appt_end
  const newDate = moment(updatedData.appointment_start_date).format(
    'YYYY-MM-DD',
  )
  const newStartTime = moment(updatedData.appointment_start_date).format(
    'HH:mm',
  )
  const newEndTime = moment(updatedData.appointment_end_date).format('HH:mm')

  if (
    prevDate === newDate &&
    prevStartTime === newStartTime &&
    prevEndTime === newEndTime &&
    (previousData
      ? previousData.operatory_id === updatedData.operatory_id
      : true)
  ) {
    args.cancel = true
    return true
  }
  return false
}

function WeekCalendar({
  selectDate,
  appointmentList,
  providerList,
  operatoryList,
  isHipaa,
  apptModalToggle,
  setAppointmentDetails,
  onViewLoaded,
  heightOffset = 10,
  onViewNote,
  schedulerSettings = INITIAL_SCHEDULER_SETTINGS_DATA,
  allowDragAndDrop = true,
  allowResizing = true,
  aiActorId: aiActorIdProp,
}: WeekCalendarProps): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>()
  const scheduleObj = useRef<ScheduleComponent>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const subscribedActorId = useAiReceptionistActorId()
  const aiActorId =
    aiActorIdProp !== undefined ? aiActorIdProp : subscribedActorId
  const recipientEmail = useRef<string>('')

  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  const [apptIdToDelete, setApptIdToDelete] = useState<string | null>(null)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [patientName, setPatientName] = useState('')
  const [draggedEvent, setDraggedEvent] = useState<AppointmentResponse | null>(
    null,
  )
  const [availableProviderList, setAvailableProviderList] = useState<any[]>([])
  const [previousOperatoryId, setPreviousOperatoryId] = useState<string | null>(
    null,
  )
  const [modalState, setModalState] = useState<IModalState>({
    deleteModal: false,
    callModal: false,
    callConfirmation: false,
    alertModal: false,
    dragAndDropModal: false,
  })
  const [slotUnavailableContent, setSlotUnavailableContent] = useState(
    getSlotUnavailableContent(),
  )
  const [showEmailSender, setShowEmailSender] = useState(false)
  const [height, setHeight] = useState(0)

  const isHipaaRef = useRef(isHipaa)
  isHipaaRef.current = isHipaa

  const schedulerSettingsRef = useRef(schedulerSettings)
  schedulerSettingsRef.current = schedulerSettings

  const appointmentListRef = useRef(appointmentList)
  appointmentListRef.current = appointmentList ?? []

  const onViewLoadedRef = useRef(onViewLoaded)
  onViewLoadedRef.current = onViewLoaded

  const viewLoadedCalledRef = useRef(false)
  const scrollToDefaultCalledRef = useRef(false)

  const currentScrollPosition = useRef({ scrollTop: 0, scrollLeft: 0 })

  const safeAppointmentList = appointmentList ?? []
  const scheduleIsReady = height > 0
  const scheduleHeight = scheduleIsReady ? height : window.innerHeight - 146

  const selectedDate = useMemo(() => {
    const parsed = moment(selectDate, 'MMMM D, YYYY')
    return parsed.isValid() ? parsed.toDate() : new Date(selectDate)
  }, [selectDate])

  useEffect(() => {
    const stage = document.querySelector('.appt-calendar-stage')
    const apptContainer = document.querySelector('.appt-container')
    const element =
      stage instanceof HTMLElement
        ? stage
        : apptContainer instanceof HTMLElement
          ? apptContainer
          : null

    if (!element) return

    const measureHeight = (): void => {
      const nextHeight =
        Math.round(element.getBoundingClientRect().height) - 56 - heightOffset
      setHeight((prev) => (prev !== nextHeight ? nextHeight : prev))
    }

    measureHeight()

    const observer = new ResizeObserver(() => {
      measureHeight()
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [heightOffset])

  const updateTimeScaleFontSize = useCallback(() => {
    const update = (): void => {
      const els = document.querySelectorAll<HTMLElement>(
        '.e-schedule-table .time-scale',
      )
      const els2 = document.querySelector<HTMLElement>(
        '.e-schedule-table .e-work-cells',
      )

      if (!els.length || !els2) return

      const fontSize =
        els2.offsetHeight * (60 / schedulerSettings.time_slot_interval) * 0.4

      els.forEach((el) => {
        el.style.setProperty(
          'font-size',
          `${Math.min(fontSize, 12)}px`,
          'important',
        )
      })
    }

    // Run immediately if the elements already exist
    update()

    // Watch for elements rendered later
    const observer = new MutationObserver(() => {
      const els = document.querySelectorAll('.e-schedule-table .time-scale')
      const els2 = document.querySelector('.e-schedule-table .e-work-cells')

      if (els.length && els2) {
        update()
        observer.disconnect()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [schedulerSettings.time_slot_interval])

  useEffect(() => {
    if (!scheduleIsReady) return
    if (schedulerSettings.view_mode === 'Fixed') return
    let cancelled = false
    let timeoutId = 0
    const rafId = requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => {
        if (cancelled) return
        scheduleObj.current?.refresh()
        updateTimeScaleFontSize()
      }, 0)
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      window.clearTimeout(timeoutId)
    }
  }, [height, scheduleIsReady, schedulerSettings, updateTimeScaleFontSize])

  const getCurrentScrollPosition = useCallback((): void => {
    const contentElement = document.querySelector(
      '.week-calendar-container .e-content-wrap',
    )
    currentScrollPosition.current = {
      scrollTop: contentElement?.scrollTop ?? 0,
      scrollLeft: contentElement?.scrollLeft ?? 0,
    }
  }, [])

  const toggleModal = useCallback(
    (modalName: keyof IModalState, value: boolean) => {
      setModalState((prev) => ({ ...prev, [modalName]: value }))
    },
    [],
  )

  const handleOpenModal = useCallback(
    (props: AppointmentResponse) => {
      apptModalToggle(true, props)
    },
    [apptModalToggle],
  )

  const toggleAlertModal = useCallback(
    (open: boolean) => {
      if (!open) {
        setDraggedEvent(null)
        setAvailableProviderList([])
      }
      toggleModal('alertModal', open)
    },
    [toggleModal],
  )

  const openDeleteModal = useCallback(
    (apptId?: string) => {
      if (apptId) setApptIdToDelete(apptId)
      toggleModal('deleteModal', true)
    },
    [toggleModal],
  )

  const openEmailModal = useCallback((email: string) => {
    recipientEmail.current = email
    setShowEmailSender(true)
  }, [])

  const handleUpdateAppointmentStatus = useCallback(
    async (appointment_id: string, status: number) => {
      await dispatch(updateAppointmentStatus({ appointment_id, status }))
    },
    [dispatch],
  )

  const openCallConfirmationModal = useCallback(
    (number: string, name: string) => {
      setPhoneNumber(number)
      setPatientName(name)
      toggleModal('callConfirmation', true)
    },
    [toggleModal],
  )

  const closeDeleteModal = useCallback(
    () => toggleModal('deleteModal', false),
    [toggleModal],
  )

  const toggleCallModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      callModal: !prev.callModal,
      callConfirmation: false,
    }))
  }, [])

  const closeCallConfirmationModal = useCallback(
    () => toggleModal('callConfirmation', false),
    [toggleModal],
  )

  const closeDragAndDropModal = useCallback(() => {
    toggleModal('dragAndDropModal', false)
  }, [toggleModal])

  const clearDragAndDropModalState = useCallback(() => {
    setDraggedEvent(null)
    setAvailableProviderList([])
    setPreviousOperatoryId(null)
  }, [])

  const handleRenderCell = useCallback((args: RenderCellEventArgs) => {
    if (args.elementType !== 'workCells' || !args.element || !args.date) return

    const cellDate = moment(args.date).format('YYYY-MM-DD')
    const today = moment().format('YYYY-MM-DD')

    if (cellDate === today) {
      args.element.classList.add('current-day-cell')
    }
  }, [])

  const handlePopupOpen = useCallback(
    (args: PopupOpenEventArgs) => {
      if (args.type === 'Editor') {
        args.cancel = true
        if (args.data?.appointment_id) {
          handleOpenModal(args.data as AppointmentResponse)
        } else {
          const startDate = new Date(args.data?.appointment_start_date)
          const endDate = new Date(args.data?.appointment_end_date)
          const dateFormat = 'YYYY-MM-DD HH:mm:ss'
          const roomId = args.data?.operatory_id || ''
          const room = operatoryList.find(
            (operatory) => operatory.operatory_id === roomId,
          )
          const appointedProviderId = firstAppointedProviderId(
            room?.operatory_providers,
            providerList.map((provider) => provider.provider_id),
          )

          setAppointmentDetails({
            startTime: formatDateTime(startDate, dateFormat),
            endTime: formatDateTime(endDate, dateFormat),
            room: roomId,
            provider: appointedProviderId ?? '',
          })
          apptModalToggle(true)
        }
      }
      if (args.type === 'QuickInfo') {
        args.cancel = true
      }
    },
    [
      apptModalToggle,
      handleOpenModal,
      operatoryList,
      providerList,
      setAppointmentDetails,
    ],
  )

  const showSlotUnavailableAlert = useCallback(
    (
      status?: AvailabilityStatus,
      names?: { patientName?: string; providerName?: string },
    ) => {
      setSlotUnavailableContent(getSlotUnavailableContent(status, names))
      toggleAlertModal(true)
    },
    [toggleAlertModal],
  )

  const onDrag = useCallback(() => {
    if (schedulerSettings.view_mode === 'Fixed') return
    requestAnimationFrame(() => {
      const clone = document.querySelector(
        '.e-schedule .e-appointment.e-drag-clone',
      )
      if (!(clone instanceof HTMLElement)) return

      const cell = document.querySelector('.e-work-cells')
      if (!(cell instanceof HTMLElement)) return

      const actualHeight = cell.getBoundingClientRect().height
      const roundedHeight = cell.offsetHeight || Math.round(actualHeight)
      if (!roundedHeight) return

      const currentTop = parseFloat(clone.style.top || '0')
      const slotIndex = currentTop / roundedHeight
      const correction = (actualHeight - roundedHeight) * slotIndex

      clone.style.transform = `translateY(${correction}px)`
    })
  }, [schedulerSettings.view_mode])

  const appointmentDataBeforeChangeRef = useRef<any | null>(null)
  const onDragStart = useCallback((args: any) => {
    appointmentDataBeforeChangeRef.current = args.data
    const mouseEvent = args.event?.event as MouseEvent
    if (args.event && mouseEvent?.buttons !== 1) {
      args.cancel = true
    }
  }, [])
  const isBlockedAppointmentTimeSlot = useCallback((args: any): boolean => {
    const toTime = (v: any): number =>
      v instanceof Date ? v.getTime() : new Date(v).getTime()
    const newStart = toTime(args.data.appointment_start_date)
    const newEnd = toTime(args.data.appointment_end_date)
    const blockEvents = scheduleObj.current?.getBlockEvents()
    const isBlocked = blockEvents?.some((ev) => {
      const s = toTime(ev.appointment_start_date)
      const e = toTime(ev.appointment_end_date)
      return newStart < e && newEnd > s
    })
    return isBlocked ?? false
  }, [])

  const onDragStop = useCallback(
    async (args: any) => {
      if (isBlockedAppointmentTimeSlot(args)) {
        args.cancel = true
        return
      }
      const previousData = appointmentDataBeforeChangeRef.current
      appointmentDataBeforeChangeRef.current = null
      getCurrentScrollPosition()
      const updatedData: AppointmentResponse = args.data
      const targetCellDetails = scheduleObj.current?.getCellDetails(args.target)

      if (!targetCellDetails) {
        args.cancel = true
        return
      }
      if (cancelIfNoChanges(args, previousData)) return

      const startTime = moment(updatedData.appointment_start_date).format(
        'HH:mm',
      )
      let endTime = moment(updatedData.appointment_end_date).format('HH:mm')
      args.cancel = true

      if (startTime === endTime) {
        endTime = moment(updatedData.appointment_start_date)
          .add(schedulerSettings.time_slot_interval, 'minutes')
          .format('HH:mm')
      }

      let duration = Number(updatedData.duration)
      if (duration === 0) {
        duration = schedulerSettings.time_slot_interval
      }

      const responseAvailableProviders = await availableProviders({
        patient_id: updatedData.patient_id,
        operatory_id: updatedData.operatory_id,
        date: moment(updatedData.appointment_start_date).format('YYYY-MM-DD'),
        start_time: startTime,
        end_time: endTime,
        duration: duration,
        exclude_appointment_id: updatedData.appointment_id,
      })

      const providers = responseAvailableProviders.data.providers ?? []
      const availabilityStatus = providers[0]?.availability_status

      if (
        availabilityStatus === AvailabilityStatus.PATIENT_ALREADY_BOOKED ||
        availabilityStatus === AvailabilityStatus.ROOM_ALREADY_BOOKED
      ) {
        args.cancel = true
        setDraggedEvent(null)
        setAvailableProviderList([])
        queueMicrotask(() =>
          showSlotUnavailableAlert(
            availabilityStatus,
            slotUnavailableNamesFromAppointment(updatedData, providerList),
          ),
        )
        return
      }

      if (!canConfirmMoveAppointment(providers, updatedData.provider_id)) {
        args.cancel = true
        setDraggedEvent(null)
        setAvailableProviderList([])
        queueMicrotask(() => {
          setSlotUnavailableContent(noAvailableProvidersContent)
          toggleAlertModal(true)
        })
        return
      }

      args.cancel = true
      setPreviousOperatoryId(previousData?.operatory_id ?? null)
      setDraggedEvent(updatedData)
      setAvailableProviderList(providers)
      toggleModal('dragAndDropModal', true)
    },
    [
      getCurrentScrollPosition,
      schedulerSettings.time_slot_interval,
      showSlotUnavailableAlert,
      toggleAlertModal,
      toggleModal,
      isBlockedAppointmentTimeSlot,
      providerList,
    ],
  )

  const onResizeStart = useCallback((args: any) => {
    const mouseEvent = args.event as MouseEvent
    if (args.event && mouseEvent?.buttons !== 1) {
      args.cancel = true
    }
  }, [])

  const onResizeStop = useCallback(
    async (args: any) => {
      getCurrentScrollPosition()
      if (isBlockedAppointmentTimeSlot(args)) {
        args.cancel = true
        return
      }
      const updatedData: AppointmentResponse = args.data
      if (cancelIfNoChanges(args)) return

      const startTime = moment(updatedData.appointment_start_date).format(
        'HH:mm',
      )
      let endTime = moment(updatedData.appointment_end_date).format('HH:mm')
      args.cancel = true

      if (startTime === endTime) {
        endTime = moment(updatedData.appointment_start_date)
          .add(schedulerSettings.time_slot_interval, 'minutes')
          .format('HH:mm')
      }

      let duration = Number(updatedData.duration)
      if (duration === 0) {
        duration = schedulerSettings.time_slot_interval
      }

      const responseAvailableProviders = await availableProviders({
        patient_id: updatedData.patient_id,
        operatory_id: updatedData.operatory_id,
        provider_id: updatedData.provider_id,
        date: moment(updatedData.appointment_start_date).format('YYYY-MM-DD'),
        start_time: startTime,
        end_time: endTime,
        duration: duration,
        exclude_appointment_id: updatedData.appointment_id,
      })

      const providers = responseAvailableProviders.data?.providers ?? []
      const availabilityStatus = providers[0]?.availability_status

      if (availabilityStatus !== AvailabilityStatus.AVAILABLE) {
        args.cancel = true
        setDraggedEvent(null)
        setAvailableProviderList([])
        queueMicrotask(() =>
          showSlotUnavailableAlert(
            availabilityStatus,
            slotUnavailableNamesFromAppointment(updatedData, providerList),
          ),
        )
        return
      }

      const apptStart = moment(updatedData.appointment_start_date)
      const apptEnd = moment(updatedData.appointment_end_date)

      dispatch(
        updateAppointment({
          appointment_id: updatedData.appointment_id,
          updates: {
            appt_date: apptStart.format('YYYY-MM-DD'),
            appt_start: apptStart.format('HH:mm'),
            appt_end: apptEnd.format('HH:mm'),
            duration: apptEnd.diff(apptStart, 'minutes'),
          } as Partial<AppointmentRequest>,
          updateByDragAndDrop: true,
        }),
      )
    },
    [
      dispatch,
      getCurrentScrollPosition,
      schedulerSettings.time_slot_interval,
      showSlotUnavailableAlert,
      isBlockedAppointmentTimeSlot,
      providerList,
    ],
  )

  const aiActorIdRef = usePaintAppointmentAiIcons(
    containerRef,
    scheduleObj,
    safeAppointmentList,
    aiActorId,
  )

  const onEventRendered = useCallback(
    (args: any) => {
      handleEventRendered(args, schedulerSettings, aiActorIdRef.current)
    },
    [schedulerSettings, aiActorIdRef],
  )

  const dateHeaderTemplate = useCallback(
    (props: any) => CustomDateTemplate(props, appointmentListRef.current),
    [],
  )

  const eventSettings = useMemo<EventSettingsModel>(
    () => ({
      dataSource: safeAppointmentList,
      fields: CUSTOM_FIELDS,
      enableTooltip: false,
    }),
    [safeAppointmentList],
  )

  const timeScale = useMemo<TimeScaleModel>(
    () => ({
      enable: true,
      interval: 60,
      slotCount: Math.max(
        1,
        Math.floor(60 / schedulerSettings.time_slot_interval),
      ),
      majorSlotTemplate,
    }),
    [schedulerSettings.time_slot_interval],
  )

  useEffect(() => {
    viewLoadedCalledRef.current = false
    scrollToDefaultCalledRef.current = false
  }, [selectDate, safeAppointmentList])

  useEffect(() => {
    const customHeaderElements = document.querySelectorAll(
      '.week-calendar-container .week-header-row .custom-header-count',
    )
    if (customHeaderElements.length > 0) {
      scheduleObj.current?.getCurrentViewDates().forEach((date) => {
        const appointments = safeAppointmentList.filter(
          (appt) =>
            moment(appt.appt_date).format('MM/DD/YYYY') ===
              moment(date).format('MM/DD/YYYY') &&
            !('IsBlock' in (appt as any)),
        )
        const headerElem = Array.from(customHeaderElements).find(
          (elem: Element) =>
            elem.classList.contains(
              moment(date).format('DD').replace(' ', '-'),
            ),
        )
        if (headerElem) {
          headerElem.textContent = appointments.length.toString()
        }
      })
    }
  }, [safeAppointmentList])

  const notifyViewLoaded = useCallback(() => {
    if (viewLoadedCalledRef.current) return
    viewLoadedCalledRef.current = true
    onViewLoadedRef.current?.()
  }, [])

  const initialScrollDone = useRef(false)
  const handleDataBound = useCallback(() => {
    window.setTimeout(() => {
      if (!initialScrollDone.current) {
        initialScrollDone.current = true
        scheduleObj.current?.scrollTo('08:00')
      }
      notifyViewLoaded()
    }, VIEW_LOADED_DELAY_MS)
  }, [notifyViewLoaded])

  const handleEventClick = useCallback(() => {
    getCurrentScrollPosition()
  }, [getCurrentScrollPosition])

  const handleEmailSenderClose = useCallback(() => {
    setShowEmailSender(false)
  }, [])

  const handleDeleteAppointment = useCallback(
    async (apptId: string) => {
      const appointment = safeAppointmentList.find(
        (appt) => appt.appointment_id === apptId,
      )
      showLoading({
        container: '.appointment-delete-modal',
        hideElements: ['.calendar-container'],
      })
      try {
        await dispatch(deleteAppointment(apptId))
        toggleModal('deleteModal', false)
        window.setTimeout(() => {
          hideLoading('.appointment-delete-modal')
          showToast(
            createCanceledAppointmentToast(
              appointment?.appt_date ?? '',
              appointment?.appt_start ?? '',
            ),
          )
        }, 1000)
      } catch (e) {
        logger.error('Error saving appointment:', e)
        toggleModal('deleteModal', false)
        window.setTimeout(() => {
          hideLoading('.appointment-delete-modal')
          showToast(
            createFailedToast(
              'Appointment not canceled',
              'Appointment not canceled. Please try again.',
            ),
          )
        }, 1000)
      }
    },
    [
      safeAppointmentList,
      showLoading,
      dispatch,
      toggleModal,
      hideLoading,
      showToast,
    ],
  )

  const cancelScheduleText = getCancelScheduleText(
    apptIdToDelete ?? '',
    safeAppointmentList as unknown as AppointmentResponse[],
  )

  const weekRightClickMenu = useMemo(
    () => (
      <RightClickMenu
        scheduleObj={scheduleObj}
        openCallConfirmationModal={openCallConfirmationModal}
        openDeleteModal={openDeleteModal}
        openEmailModal={openEmailModal}
        handleUpdateAppointmentStatus={handleUpdateAppointmentStatus}
        onItemSelected={getCurrentScrollPosition}
        targetCalendarContainer=".week-calendar-container .e-schedule"
        onViewNote={onViewNote}
      />
    ),
    [
      openCallConfirmationModal,
      openDeleteModal,
      openEmailModal,
      handleUpdateAppointmentStatus,
      getCurrentScrollPosition,
      onViewNote,
    ],
  )

  return (
    <div
      ref={containerRef}
      className={`week-calendar-container ${schedulerSettings.view_mode === 'Full' ? 'full' : ''}`}
    >
      <ScheduleComponent
        ref={scheduleObj}
        cssClass="schedule-month-view"
        width="100%"
        height={`${scheduleHeight}px`}
        selectedDate={selectedDate}
        eventSettings={eventSettings}
        timeScale={timeScale}
        showTimeIndicator
        dateHeaderTemplate={dateHeaderTemplate}
        renderCell={handleRenderCell}
        popupOpen={handlePopupOpen}
        allowKeyboardInteraction={false}
        allowDragAndDrop={allowDragAndDrop}
        allowResizing={allowResizing}
        drag={onDrag}
        dragStart={onDragStart}
        dragStop={onDragStop}
        resizeStart={onResizeStart}
        resizeStop={onResizeStop}
        workDays={[0, 1, 2, 3, 4, 5, 6]}
        firstDayOfWeek={0}
        eventRendered={onEventRendered}
        dataBound={handleDataBound}
        eventClick={handleEventClick}
      >
        <ViewsDirective>
          <ViewDirective
            option="WorkWeek"
            {...(schedulerSettings.dateSchedule
              ? {
                  startHour:
                    schedulerSettings.dateSchedule?.[0].schedule.scheduler_hour_from
                      .split(':')
                      .slice(0, 2)
                      .join(':'),
                  endHour:
                    schedulerSettings.dateSchedule?.[0].schedule.scheduler_hour_to
                      .split(':')
                      .slice(0, 2)
                      .join(':'),
                }
              : {
                  startHour:
                    schedulerSettings.working_hours_from
                      .toString()
                      .padStart(2, '0') + ':00',
                  endHour:
                    schedulerSettings.working_hours_to
                      .toString()
                      .padStart(2, '0') + ':00',
                })}
            isSelected={false}
          />
        </ViewsDirective>
        <Inject services={[...INJECT_SERVICES]} />
      </ScheduleComponent>

      {scheduleIsReady ? weekRightClickMenu : null}

      <DeleteConfirmationModal
        show={modalState.deleteModal}
        onHide={closeDeleteModal}
        onConfirm={() => {
          if (apptIdToDelete) void handleDeleteAppointment(apptIdToDelete)
        }}
        confirmDisabled={!apptIdToDelete}
        confirmButtonText="Delete"
        title={cancelScheduleText.title}
        bodyText={cancelScheduleText.description}
      />

      {draggedEvent ? (
        <MoveAppointmentModal
          show={modalState.dragAndDropModal}
          onHide={closeDragAndDropModal}
          onExited={clearDragAndDropModalState}
          data={draggedEvent}
          previousOperatoryId={previousOperatoryId}
          dispatch={dispatch}
          providerList={providerList}
          availableProviderList={availableProviderList}
          operatoryList={operatoryList}
          onViewDetails={handleOpenModal}
        />
      ) : null}

      <RescheduleUnavailableModal
        show={modalState.alertModal}
        onHide={() => toggleAlertModal(false)}
        title={slotUnavailableContent.title}
        description={slotUnavailableContent.description}
      />

      <CallConfirmation
        show={modalState.callConfirmation}
        onHide={closeCallConfirmationModal}
        confirm={toggleCallModal}
        name={patientName}
      />

      <CallerModal
        show={modalState.callModal}
        onHide={toggleCallModal}
        phoneNumber={phoneNumber}
      />

      <EmailSender
        key={recipientEmail.current}
        show={showEmailSender}
        onHide={handleEmailSenderClose}
        recipientEmail={recipientEmail.current}
      />
    </div>
  )
}

function areWeekCalendarPropsEqual(
  prev: WeekCalendarProps,
  next: WeekCalendarProps,
): boolean {
  return (
    prev.selectDate === next.selectDate &&
    prev.appointmentList === next.appointmentList &&
    prev.providerList === next.providerList &&
    prev.operatoryList === next.operatoryList &&
    prev.isHipaa === next.isHipaa &&
    prev.heightOffset === next.heightOffset &&
    prev.schedulerSettings === next.schedulerSettings &&
    prev.apptModalToggle === next.apptModalToggle &&
    prev.setAppointmentDetails === next.setAppointmentDetails &&
    prev.onViewLoaded === next.onViewLoaded &&
    prev.onViewNote === next.onViewNote &&
    prev.allowDragAndDrop === next.allowDragAndDrop &&
    prev.allowResizing === next.allowResizing &&
    prev.aiActorId === next.aiActorId
  )
}

export default memo(WeekCalendar, areWeekCalendarPropsEqual)
